import type {GridItemStorage, GridStockMediaItemBackendData} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {GridStockImageItemBackEndData} from '@Components/base-grid/components/grid-stock-image-item';
import type {GridAiIconItemBackEndData, GridIconItemBackEndData} from '@Components/base-grid/components/grid-icon-item/grid-icon-item.types';
import type {GridAiStickerItemBackEndData, GridStillStickerItemBackEndData} from '@Components/base-grid/components/grid-still-sticker-item/grid-still-sticker-item.types';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import type {StockGetterOptions} from '@Libraries/pmw-stock-media-library';
import {getSerializableStockItem, StockSources} from '@Libraries/pmw-stock-media-library';
import {SOURCE} from '@Libraries/stock-graphic-library';
import type {PMWStockBackendAiAnimatedSticker, PMWStockBackendAnimatedSticker, PMWStockBackendShape} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import {
  aiAnimatedStickerDataToAiAnimatedStickerItem,
  aiIconDataToAiIconItem,
  aiStickerDataToAiStickerItem,
  animatedStickerDataToAnimatedStickerItem,
  extractedGettyStickerDataToStickerItem,
  getAiCarouselItemMeta,
  getGridItemForBackendData,
  getOptionsForStockGetter,
  iconDataToIconItem,
  imageDataToImageItem,
  isAiEditedImageGridItem,
  isAnySubFilterSelected,
  PMW_ITEM_TYPE_TO_STOCK_SOURCE,
  shapeDataToShapeItem,
  stillStickerDataToStickerItem,
} from '@Panels/stock-panel/stock-panel.library';
import {getTieredSourcesForSelectedTab} from '@Panels/stock-panel/stock-panel-request-params.helper';
import type {GridItemBaseStorageBackendData, SourceTier} from '@Panels/stock-panel/stock-panel.types';
import {AUDIO_CATEGORY_FILTER_IDS} from '@Panels/stock-panel/stock-panel.types';
import type {
  StockFavoriteBackendItem,
  StockFavoriteIdentity,
  StockFavoriteKey,
  StockFavoriteSource,
  StockFavoritesPageRequest,
  StockFavoritesPageResponse,
  StockFavoritesProbePage,
  StockFavoritesQuery,
  StockFavoritesQueryParams,
} from '@Panels/stock-panel/stock-favorites.types';
import {FAVORITES_LIMIT_REACHED_ERROR_CODE, FAVORITES_PILL_SOURCES_BY_TAB, FAVORITES_SOURCE_FILTERS} from '@Panels/stock-panel/stock-favorites.types';
import {assertNever} from '@Utils/assert-never.util';

const IMAGE_SOURCE_BY_PROVIDER: Partial<Record<string, StockFavoriteSource>> = {
  [SOURCE.GETTY]: StockSources.GETTY_IMAGE,
  [SOURCE.STORYBLOCKS]: StockSources.STORYBLOCK_IMAGE,
};

const VIDEO_SOURCE_BY_PROVIDER: Partial<Record<string, StockFavoriteSource>> = {
  [SOURCE.GETTY]: StockSources.GETTY_VIDEO,
  [SOURCE.STORYBLOCKS]: StockSources.STORYBLOCK_VIDEO,
};

const CLIPART_SOURCE_BY_PROVIDER: Partial<Record<string, StockFavoriteSource>> = {
  [SOURCE.GETTY_ILLUSTRATIONS]: StockSources.GETTY_ILLUSTRATION,
  [SOURCE.STORYBLOCKS_ILLUSTRATIONS]: StockSources.STORYBLOCK_ILLUSTRATION,
};

/** The catalogue source behind each PMW tile type; the tile's id is the `pmw_stock_media` id for all of them. */
const PMW_SOURCE_BY_GRID_ITEM_TYPE: Partial<Record<GRID_ITEM_TYPE, StockFavoriteSource>> = {
  ...PMW_ITEM_TYPE_TO_STOCK_SOURCE,
  [GRID_ITEM_TYPE.SHAPE]: StockSources.PMW_SHAPE,
  [GRID_ITEM_TYPE.ICON]: StockSources.PMW_ICON,
  [GRID_ITEM_TYPE.STILL_STICKER]: StockSources.PMW_STILL_STICKER,
  [GRID_ITEM_TYPE.ANIMATED_STICKER]: StockSources.PMW_ANIMATED_STICKER,
};

/** Tile ids are typed as strings, but the PMW converters store the numeric media id; the wire id is always a string. */
const toItemId = (id: unknown): string => {
  return String(id);
};

/** The user's own AI generations are not favoritable: the AI carousel picks, the AI grid's tiles and AI-edited images. */
const isAiGeneratedGridItem = (item: GridItemStorage): boolean => {
  return getAiCarouselItemMeta(item) !== undefined || item.source === SOURCE.AI_TEXT_TO_IMAGE || isAiEditedImageGridItem(item);
};

/**
 * The favorite a grid item would become, or null for a tile that cannot be favorited: buttons and skeletons,
 * AI generations, and Pixabay items (no tile renders them any more). An outside provider's item carries its
 * snapshot; a PMW item carries only its catalogue id.
 */
export const getStockFavoriteIdentity = (item: GridItemStorage): StockFavoriteIdentity | null => {
  if (isAiGeneratedGridItem(item)) {
    return null;
  }

  switch (item.type) {
    case GRID_ITEM_TYPE.STOCK_IMAGE:
      return item.source === SOURCE.PMW_IMAGE ? {itemSource: StockSources.PMW_STOCK_IMAGE, itemId: toItemId(item.id)} : getProviderFavoriteIdentity(item, IMAGE_SOURCE_BY_PROVIDER);
    case GRID_ITEM_TYPE.STOCK_VIDEO:
      return getProviderFavoriteIdentity(item, VIDEO_SOURCE_BY_PROVIDER);
    case GRID_ITEM_TYPE.CLIPART:
      return getProviderFavoriteIdentity(item, CLIPART_SOURCE_BY_PROVIDER);
    case GRID_ITEM_TYPE.STOCK_AUDIO:
      return {itemSource: StockSources.STORYBLOCK_AUDIO, itemId: toItemId(item.id), snapshot: getSerializableStockItem(item)};
    default:
      return getPmwFavoriteIdentity(item);
  }
};

const getProviderFavoriteIdentity = (item: GridItemStorage, sourceByProvider: Partial<Record<string, StockFavoriteSource>>): StockFavoriteIdentity | null => {
  const provider: unknown = item.source;
  const itemSource = typeof provider === 'string' ? sourceByProvider[provider] : undefined;
  if (itemSource === undefined) {
    return null;
  }
  return {itemSource, itemId: toItemId(item.id), snapshot: getSerializableStockItem(item)};
};

const getPmwFavoriteIdentity = (item: GridItemStorage): StockFavoriteIdentity | null => {
  const itemSource = PMW_SOURCE_BY_GRID_ITEM_TYPE[item.type];
  if (itemSource === undefined) {
    return null;
  }
  return {itemSource, itemId: toItemId(item.id)};
};

/** The favorites a set of grid items would become, leaving out the tiles that cannot be favorited. */
export const getStockFavoriteIdentities = (items: GridItemStorage[]): StockFavoriteIdentity[] => {
  return items.map(getStockFavoriteIdentity).filter((identity): identity is StockFavoriteIdentity => identity !== null);
};

/**
 * The items a multi-selection's heart toggles: all of them when every one is already a favorite, so the tap
 * removes them; otherwise only the ones that are not favorites yet, so the tap adds them and removes nothing.
 */
export const getStockFavoritesToToggleTogether = (identities: StockFavoriteIdentity[], isFavorited: (key: StockFavoriteKey) => boolean): StockFavoriteIdentity[] => {
  const identitiesNotFavorited = identities.filter((identity) => !isFavorited(identity));
  return identitiesNotFavorited.length === 0 ? identities : identitiesNotFavorited;
};

export const getStockFavoriteToggleErrorMessage = (errorCode: unknown): string => {
  return errorCode === FAVORITES_LIMIT_REACHED_ERROR_CODE ? window.i18next.t('pmwjs_favorites_limit_reached') : window.i18next.t('pmwjs_error_occurred_try_again');
};

/**
 * Turns a favorite the backend hydrated back into the tile its source normally renders, through the same
 * converters the stock grid uses.
 */
export const getGridItemForStockFavorite = (favorite: StockFavoriteBackendItem): GridItemStorage => {
  const {backendData} = favorite;
  switch (favorite.itemSource) {
    case StockSources.PMW_SHAPE:
      return shapeDataToShapeItem(backendData as PMWStockBackendShape);
    case StockSources.PMW_ICON:
      return iconDataToIconItem(backendData as GridIconItemBackEndData);
    case StockSources.PMW_AI_ICON:
      return aiIconDataToAiIconItem(backendData as GridAiIconItemBackEndData);
    case StockSources.PMW_STOCK_IMAGE:
      return imageDataToImageItem(backendData as GridStockImageItemBackEndData, StockSources.PMW_STOCK_IMAGE);
    case StockSources.PMW_STILL_STICKER:
      return stillStickerDataToStickerItem(backendData as GridStillStickerItemBackEndData);
    case StockSources.PMW_AI_STICKER:
      return aiStickerDataToAiStickerItem(backendData as GridAiStickerItemBackEndData);
    case StockSources.PMW_ANIMATED_STICKER:
      return animatedStickerDataToAnimatedStickerItem(backendData as PMWStockBackendAnimatedSticker);
    case StockSources.PMW_AI_ANIMATED_STICKER:
      return aiAnimatedStickerDataToAiAnimatedStickerItem(backendData as PMWStockBackendAiAnimatedSticker);
    case StockSources.PMW_EXTRACTED_GETTY_STICKER:
      return extractedGettyStickerDataToStickerItem(backendData as GridStockMediaItemBackendData);
    case StockSources.GETTY_IMAGE:
    case StockSources.GETTY_VIDEO:
    case StockSources.GETTY_ILLUSTRATION:
    case StockSources.STORYBLOCK_IMAGE:
    case StockSources.STORYBLOCK_VIDEO:
    case StockSources.STORYBLOCK_ILLUSTRATION:
    case StockSources.STORYBLOCK_AUDIO:
      return getGridItemForBackendData(backendData as GridItemBaseStorageBackendData);
    default:
      return assertNever(favorite.itemSource);
  }
};

/**
 * LazyMasonryGrid asks for one tile more than it renders to learn whether more exist, then drops that probe
 * tile. When the buffer can satisfy the probe, the probe tile stays at the head of the buffer so the next page
 * starts with it; when it cannot, the list is exhausted and nothing stays buffered.
 */
export const takeStockFavoritesProbePage = (buffer: GridItemStorage[], limit: number): StockFavoritesProbePage => {
  const response = buffer.slice(0, limit);
  const remainingBuffer = response.length === limit ? buffer.slice(limit - 1) : [];
  return {response, remainingBuffer};
};

export const addStockFavorite = async (identity: StockFavoriteIdentity): Promise<void> => {
  await window.PMW.writeLocal('stockmedia/addStockFavorite', {itemSource: identity.itemSource, itemId: identity.itemId, itemData: identity.snapshot});
};

export const removeStockFavorite = async (key: StockFavoriteKey): Promise<void> => {
  await window.PMW.writeLocal('stockmedia/removeStockFavorite', {itemSource: key.itemSource, itemId: key.itemId});
};

export const getStockFavoriteKeys = async (): Promise<StockFavoriteKey[]> => {
  return (await window.PMW.readLocal('stockmedia/getStockFavoriteKeys')) as StockFavoriteKey[];
};

/**
 * jQuery leaves an empty array out of the query string, and the endpoint reads a missing `sources` as every source,
 * so a page that can match no source is answered here without a request.
 */
export const getStockFavoritesPage = async (request: StockFavoritesPageRequest): Promise<StockFavoritesPageResponse> => {
  if (request.sources.length === 0) {
    return {items: [], hasMore: false, nextCursor: null};
  }
  return (await window.PMW.readLocal('stockmedia/getStockFavorites', {...request, beforeId: request.beforeId ?? undefined})) as StockFavoritesPageResponse;
};

/**
 * What a favorites view asks the server for. The query carries every limit of the view and of its host, so the server
 * filters before it pages and the grid shows every favorite it gets. A media tab also sends its orientation, 4K,
 * category and audio pills through the same options the search sends. The Favorites tab has no pills.
 */
export const getStockFavoritesQuery = (params: StockFavoritesQueryParams): StockFavoritesQuery => {
  const sources = getFavoriteSourcesForView(params).filter((source) => !params.blacklistedSources.includes(source));
  if (FAVORITES_PILL_SOURCES_BY_TAB[params.tab] === undefined) {
    return {sources};
  }

  const searchOptions = getOptionsForStockGetter(params.selectedFilters, params.tab, [...AUDIO_CATEGORY_FILTER_IDS]);
  return {
    sources,
    orientation: searchOptions.orientation,
    onlyUhd: searchOptions.only4KVideos,
    categories: searchOptions.iconCategories ?? searchOptions.shapeCategories,
    audioCategoryIds: getAudioCategoryIdsOfOptions(searchOptions),
  };
};

/** The Storyblocks category ids the audio dropdowns picked, sorted so that the same pills always make the same query; undefined while none is set. */
const getAudioCategoryIdsOfOptions = (searchOptions: StockGetterOptions): number[] | undefined => {
  const audioCategoryIds = Object.values(searchOptions.audioFilters ?? {})
    .flat()
    .map(Number)
    .filter((categoryId) => Number.isInteger(categoryId) && categoryId > 0)
    .sort((first, second) => first - second);
  return audioCategoryIds.length > 0 ? audioCategoryIds : undefined;
};

/**
 * Reports a click on the Favorites pill as a selection or a deselection: a pill click sends its sub-filter id to turn
 * the pill on, and null to turn it off. Only a click reaches here; the panel turning the pill off on a search does not.
 */
export const trackFavoritesPillClick = (subFilterID: string | null): void => {
  trackPosterBuilderGA4Events(subFilterID === null ? GA4EventName.MEDIA_MODAL_FAVOURITES_PILL_DESELECTED : GA4EventName.MEDIA_MODAL_FAVOURITES_PILL_SELECTED);
};

/** Names a query in a grid id, so that each change of the pills mounts a new grid that pages from the newest favorite. */
export const getStockFavoritesQueryKey = (query: StockFavoritesQuery): string => {
  return JSON.stringify(query);
};

/**
 * A media tab's own sources, narrowed by its Stock source or Animated / Still pill to the sources the search would
 * use. A tab without sources of its own, such as Favorites, takes the sources of every tab the host shows.
 */
const getFavoriteSourcesForView = (params: StockFavoritesQueryParams): StockFavoriteSource[] => {
  const tabSources = FAVORITES_PILL_SOURCES_BY_TAB[params.tab];
  if (tabSources === undefined) {
    return params.tabsToShow.flatMap((tab) => FAVORITES_PILL_SOURCES_BY_TAB[tab] ?? []);
  }
  if (!isAnySubFilterSelected(params.selectedFilters, FAVORITES_SOURCE_FILTERS)) {
    return tabSources;
  }

  const searchSources = new Set(getTieredSourcesForSelectedTab(params.tab, params.selectedFilters).flatMap(getSourcesOfTier));
  return tabSources.filter((source) => searchSources.has(source));
};

const getSourcesOfTier = (tier: SourceTier): StockSources[] => {
  return [...(tier.internalStock?.stockSources ?? []), ...(tier.externalStock ?? []).map((externalStock) => externalStock.stockSource)];
};
